import React, { useState } from "react";
import {
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Typography
} from "@material-ui/core";
import {
  CustomerContractAgreementType,
  CustomerContractCompany,
  CustomerContractDocumentTypes,
  GetCustomerContractEventLog,
  GetCustomerContractProfile,
  InvoicingStrategyType,
  useGetCustomerContractProfileQuery,
  useSetCustomerContractInvoicingStrgMutation,
  useUploadCustomerContractDocMutation
} from "@graphql";
import { useLocale, useSnackbars } from "@hooks";
import { Spinner, SpinnerOverlay, Topbar } from "@components";
import { ErrorBoundary } from "@components/error-boundary";
import { TabProps, Tabs } from "@components/Tabs";
import { DnDLocalContainer } from "@containers/DnDContainer";
import { CustomerContractEventLog } from "@containers/EventLog/CustomerContractEventLog/CustomerContractEventLog";
import { CustomerContractCard } from "../CustomerContractCard/CustomerContractCard";
import { CustomerContractCustomerList } from "../CustomerContractCustomerList/CustomerContractCustomerList";
import { CustomerContractContactPerson } from "../CustomerContractContactPerson/CustomerContractContactPerson";
import { CustomerContractPriceLists } from "../CustomerContractPriceLists/CustomerContractPriceLists";
import { CustomerContractDocuments } from "../CustomerContractDocuments/CustomerContractDocuments";
import { InfoTab } from "./components/InfoFile/InfoTab";
import { store } from "@store";
import { DocumentUploader } from "@containers/DocumentUploader/DocumentUploader";
import { SnapshotsTable } from "@contracts/CustomerContractProfile/components/SnapshotsTable/SnapshotsTable";
import { SubVersionTable } from "@contracts/CustomerContractProfile/components/SubVersionsTable/SubVersionTable";

type Props = {
  id: string;
};

export function CustomerContractProfile(props: Props) {
  const { locale } = useLocale();
  const { showSuccess, showError } = useSnackbars();
  const [uploadDocs] = useUploadCustomerContractDocMutation();
  const [setInvoicing, setInvoicingOpt] = useSetCustomerContractInvoicingStrgMutation();
  const [files, setFiles] = useState<File[]>([]);

  const handleUpload = async (documentType, files) => {
    for (const file of files) {
      await uploadDocs({
        variables: {
          customerContractId: props.id,
          file,
          documentType
        },
        refetchQueries: [GetCustomerContractEventLog.OperationName]
      }).then((r) => showSuccess(`Uploaded`));
    }
  };

  const handleInvoicingStrategy = async (e, type) => {
    const answer = await store().ui.confirmNullifyAsync(
      locale.AreYouSureChangeContractInvoicing
    );
    if (!answer) {
      return;
    }

    setInvoicing({
      variables: {
        type,
        id: props.id
      },
      refetchQueries: [GetCustomerContractProfile.OperationName]
    })
      .then(() => showSuccess("Updated"))
      .catch((err) => showError(`${err}`));
  };

  const request = () => {
    store()
      .upload.requestFileUpload({ multiple: true })
      .then((files) => setFiles(files));
  };

  const { data, loading, error } = useGetCustomerContractProfileQuery({
    variables: { id: props.id },
    fetchPolicy: "network-only"
  });

  const [tab, setTab] = useState("event_log");

  if (loading) {
    return <Spinner fullHeight />;
  }

  if (error) {
    return <div>{error.toString()}</div>;
  }

  const tabs: TabProps[] = [
    {
      label: locale.EventLog,
      value: "event_log",
      content: () => <CustomerContractEventLog id={props.id} />
    },
    {
      label: "info",
      value: "info",
      content: () => <InfoTab id={props.id} />
    },
    {
      label: locale.customerContractProfile.tabDocs,
      value: "files",
      content: () => (
        <CustomerContractDocuments customerContractId={props.id} onUpload={request} />
      )
    },
    {
      label: locale.customerContractProfile.snapshots,
      value: "snapshots",
      content: () => <SnapshotsTable id={props.id} />,
      hidden:
        data?.customerContractById?.typeOfAgreement ===
          CustomerContractAgreementType.Procured ||
        data?.customerContractById.company === CustomerContractCompany.Bonliva_Care
    },
    {
      label: locale.customerContractProfile.subVersions,
      value: "subVersions",
      content: () => (
        <SubVersionTable
          id={props.id}
          isNationalProcurement={
            data?.customerContractById?.isNationalProcurement ?? false
          }
        />
      )
    }
  ];

  return (
    <DnDLocalContainer multiple onDrop={(f) => setFiles(f)} noClick>
      <DocumentUploader
        documentTypes={Object.values(CustomerContractDocumentTypes).toLabelValue(
          (v) => locale.customerContractDocumentTypes[v]
        )}
        files={files}
        onUpload={(docType) => handleUpload(docType, files)}
        onClose={() => setFiles([])}
      />

      <Grid container spacing={2} style={{ padding: 16, marginTop: -16 }}>
        <Grid item xs={12}>
          <Topbar />
          <Divider style={{ margin: "8px -16px 0" }} />

          <div style={{ marginTop: 16 }}>
            <ErrorBoundary>
              <CustomerContractCard id={props.id} hideTitle />
            </ErrorBoundary>
          </div>

          <div style={{ marginTop: 16 }}>
            <ErrorBoundary>
              <Typography variant="overline" style={{ marginLeft: 4 }}>
                {locale.customerContractProfile.organisations}
              </Typography>
              <CustomerContractCustomerList data={data.customerContractById} />
            </ErrorBoundary>
          </div>

          <div style={{ marginTop: 16 }}>
            <ErrorBoundary>
              <Typography variant="overline" style={{ marginLeft: 4 }}>
                {locale.InvoicingStrategy}
              </Typography>
              <Paper style={{ padding: "0 16px", position: "relative" }}>
                {setInvoicingOpt.loading && <SpinnerOverlay show={true} />}
                <RadioGroup
                  aria-label="invoicingStrategy"
                  name="invoicingStrategy"
                  value={data?.customerContractById?.invoicingStrategy}
                  onChange={handleInvoicingStrategy}
                >
                  {Object.values(InvoicingStrategyType).map((x) => (
                    <div>
                      <FormControlLabel
                        key={x}
                        disabled={setInvoicingOpt.loading}
                        value={x}
                        control={<Radio />}
                        label={locale.invoicingStrategyType[x]}
                      />
                    </div>
                  ))}
                </RadioGroup>
              </Paper>
            </ErrorBoundary>
          </div>

          <div style={{ marginTop: 16 }}>
            <ErrorBoundary>
              <Typography variant="overline" style={{ marginLeft: 4 }}>
                {locale.customerContractProfile.priceLists}
              </Typography>
              <CustomerContractPriceLists id={props.id} />
            </ErrorBoundary>
          </div>

          <div style={{ marginTop: 16 }}>
            <ErrorBoundary>
              <Typography variant="overline" style={{ marginLeft: 4 }}>
                {locale.customerContractProfile.contactPerson}
              </Typography>
              <CustomerContractContactPerson id={props.id} />
            </ErrorBoundary>
          </div>

          <div style={{ marginTop: 16 }}>
            <Tabs
              tabs={tabs}
              AppBarProps={{
                style: { borderTopRightRadius: 4, borderTopLeftRadius: 4 }
              }}
              TabsProps={{
                indicatorColor: "primary"
              }}
              value={tab}
              onChange={(e, v) => {
                setTab(v);
              }}
            />
          </div>
        </Grid>
      </Grid>
    </DnDLocalContainer>
  );
}
